/**
 * The approval engine's wire types.
 *
 * **THE ENUMS ARE NUMERIC, because that is what the API serializes.** No
 * `JsonStringEnumConverter` is configured, so a body carrying `"mode": "Sequential"` is refused
 * outright and a response carrying `0` never equals the string `'Pending'`. This file declared
 * string unions for a day, and the cost was not a type error — it was every comparison silently
 * false: the approval profile drew NO decision button and its banner said the record had been
 * withdrawn, on a request that was pending. The convention is `ISSAUNCE_TYPE`'s: an `as const`
 * object of numbers, with the LABEL coming from the server on the DTO.
 *
 * The FACET values are the exception and stay strings — the server emits enum NAMES there and
 * parses them with `Enum.TryParse` — so a facet selection passes straight through untouched.
 */

/** How an approver is activated. Declared per approver; a category's own value is the summary. */
export const APPROVAL_MODE = { sequential: 0, simultaneous: 1 } as const;

export type ApprovalMode = (typeof APPROVAL_MODE)[keyof typeof APPROVAL_MODE];

/** Whether an approver authorizes, or is only confirming they have seen the request. */
export const APPROVAL_DECISION = { approve: 0, acknowledge: 1 } as const;

export type ApprovalDecisionType = (typeof APPROVAL_DECISION)[keyof typeof APPROVAL_DECISION];

/** A request's lifecycle. */
export const APPROVAL_STATUS = {
  pending: 0,
  approved: 1,
  rejected: 2,
  cancelled: 3,
  retracted: 4,
} as const;

export type ApprovalRequestStatus = (typeof APPROVAL_STATUS)[keyof typeof APPROVAL_STATUS];

/** One approver's step within a request. */
export const APPROVAL_STEP_STATUS = {
  waiting: 0,
  active: 1,
  approved: 2,
  rejected: 3,
  skipped: 4,
  acknowledged: 5,
} as const;

export type ApprovalStepStatus = (typeof APPROVAL_STEP_STATUS)[keyof typeof APPROVAL_STEP_STATUS];

/** One row of the inbox. */
export interface ApprovalRequestListItem {
  id: string;
  workflowKey: string;
  workflowName: string;
  entityType: string;
  subjectId: string;
  subjectReference: string | null;
  subjectSummary: string | null;
  mode: ApprovalMode;
  status: ApprovalRequestStatus;
  statusLabel: string;
  requestedByName: string | null;
  completedAt: string | null;
  outcomeReason: string | null;
  signedSteps: number;
  totalSteps: number;
  /** Whether THIS caller has a step awaiting their decision. Resolved per request. */
  awaitingMe: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface ApprovalStep {
  id: string;
  approverUserId: string;
  approverName: string | null;
  sequenceOrder: number;
  mode: ApprovalMode;
  modeLabel: string;
  decisionType: ApprovalDecisionType;
  decisionTypeLabel: string;
  status: ApprovalStepStatus;
  statusLabel: string;
  decidedAt: string | null;
  decisionReason: string | null;
}

/**
 * What a subject's value IS, so the page formats it the way this product formats it.
 *
 * **The VALUE arrives RAW** — an amount as `1100.00`, a date as an ISO string — and the page
 * runs `formatMoney` or `formatDate` on it. Formatting server-side would put a second money
 * formatter in the product, and the same figure would group one way here and another on the
 * module's own page.
 *
 * Numeric on the wire, like every other enum this API sends.
 */
export const SUBJECT_VALUE = {
  text: 0,
  code: 1,
  money: 2,
  quantity: 3,
  date: 4,
  dateTime: 5,
} as const;

export type ApprovalSubjectValueKind = (typeof SUBJECT_VALUE)[keyof typeof SUBJECT_VALUE];

/** One label/value fact of a subject, as its own module states it. */
export interface ApprovalSubjectFact {
  label: string;
  value: string | null;
  kind: ApprovalSubjectValueKind;
}

/**
 * One titled group of a subject's facts.
 *
 * Sections rather than one flat list, which is the reference's own shape: a subject of any
 * size reads as one undifferentiated column otherwise.
 */
export interface ApprovalSubjectSection {
  title: string;
  facts: ApprovalSubjectFact[];
}

export interface ApprovalSubjectColumn {
  header: string;
  width: number;
  kind: ApprovalSubjectValueKind;
}

export interface ApprovalSubjectTable {
  heading: string;
  columns: ApprovalSubjectColumn[];
  /** One list of cells per row, positionally matching `columns`. */
  rows: (string | null)[][];
  empty: string;
}

/**
 * What the subject SAYS, composed by the owning module's approval handler.
 *
 * **Deliberately generic.** Nothing here names a purchase request, so one panel renders every
 * workflow's subject and adding a second workflow touches no approvals code — the same seam
 * the engine's backend contract is, carried onto the wire.
 */
export interface ApprovalSubjectDetail {
  heading: string;
  sections: ApprovalSubjectSection[];
  tables: ApprovalSubjectTable[];
}

export interface ApprovalRequestDetail {
  id: string;
  workflowKey: string;
  workflowName: string;
  entityType: string;
  subjectId: string;
  subjectReference: string | null;
  subjectSummary: string | null;
  mode: ApprovalMode;
  modeLabel: string;
  /** False only on an administrator-fallback request, where any one signature completes it. */
  requireAll: boolean;
  status: ApprovalRequestStatus;
  statusLabel: string;
  requestedByUserId: string | null;
  requestedByName: string | null;
  completedAt: string | null;
  outcomeReason: string | null;
  steps: ApprovalStep[];
  /**
   * What is being approved.
   *
   * **NULL means one of two things and the page must say which** — the API stripped it because
   * the caller does not hold `subjectReadPermission`, or the module had nothing to add to its
   * one-line summary. The page tells them apart from its own permission set; a second
   * "withheld" flag would be one fact in two slots.
   *
   * It arrives as `undefined` rather than `null` when the server omits it
   * (`DefaultIgnoreCondition = WhenWritingNull`), so every test against it is `!= null`.
   */
  subjectDetail?: ApprovalSubjectDetail | null;
  /** The key a reader needs to be shown `subjectDetail`. */
  subjectReadPermission?: string | null;
  canApprove: boolean;
  canAcknowledge: boolean;
  canRetract: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/** The inbox's own query. `scope` is a view scope rather than a facet — see the cog entry. */
export interface ApprovalListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  stage?: string;
  workflow?: string;
  scope?: 'mine' | 'all';
}

// --- Routing (the settings side) ---

export interface ApprovalWorkflowOption {
  key: string;
  name: string;
  description: string;
  entityType: string;
  /** Whether an active category already configures it. */
  isConfigured: boolean;
}

export interface ApprovalCategoryApprover {
  id: string;
  userId: string;
  userName: string | null;
  sequenceOrder: number;
  mode: ApprovalMode;
  modeLabel: string;
  decisionType: ApprovalDecisionType;
  decisionTypeLabel: string;
}

export interface ApprovalCategoryListItem {
  id: string;
  workflowKey: string;
  workflowName: string;
  mode: ApprovalMode;
  modeLabel: string;
  approverCount: number;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface ApprovalCategoryDetail extends ApprovalCategoryListItem {
  workflowDescription: string;
  approvers: ApprovalCategoryApprover[];
}

/**
 * One approver as the form sends them.
 *
 * **No position and no id.** The place in the chain is the array order, which the server
 * numbers, and the set is addressed as a whole — the server reconciles it against the rows
 * already there.
 */
export interface ApprovalApproverInput {
  userId: string;
  mode: ApprovalMode;
  decisionType: ApprovalDecisionType;
}

export interface SaveApprovalCategoryRequest {
  workflowKey: string;
  approvers: ApprovalApproverInput[];
}

/** Tone for a request's own state. */
export function approvalStatusTone(
  status: ApprovalRequestStatus,
): 'active' | 'inactive' | 'blocked' {
  switch (status) {
    case APPROVAL_STATUS.approved:
      return 'active';

    /*
      REJECTED IS `blocked`, not `pending`. Amber is the brand colour, so an index table refuses
      it — and blocked means a state that stops the record being used, which a refusal is.
    */
    case APPROVAL_STATUS.rejected:
      return 'blocked';

    /*
      PENDING, CANCELLED and RETRACTED all read `inactive`, and that is the honest cut: none of
      them is a failure and none is a completion. The LABEL carries which, as plain text in its
      own column — the disciplinary panel's split between a lifecycle and a row's own state.
    */
    default:
      return 'inactive';
  }
}

/**
 * The mode and decision-type options, in the order they read.
 *
 * `value` is a STRING because that is what `<Select>` binds; the caller converts with
 * `Number(next)`, which is the pattern the issuance form's method picker already uses.
 */
export const APPROVAL_MODE_OPTIONS: { value: string; label: string; sublabel: string }[] = [
  {
    value: String(APPROVAL_MODE.sequential),
    label: 'Sequential',
    sublabel: 'Waits for the one before',
  },
  {
    value: String(APPROVAL_MODE.simultaneous),
    label: 'Simultaneous',
    sublabel: 'Active from the start',
  },
];

export const APPROVAL_DECISION_OPTIONS: { value: string; label: string; sublabel: string }[] = [
  {
    value: String(APPROVAL_DECISION.approve),
    label: 'Approve',
    sublabel: 'Authorizes, and may reject',
  },
  {
    value: String(APPROVAL_DECISION.acknowledge),
    label: 'Acknowledge',
    sublabel: 'Confirms they have seen it',
  },
];

/**
 * Whether the approval's own conclusion is ALREADY STATED by one of its signatures.
 *
 * **A timeline draws a row per signature and a row for the outcome, and on an ordinary chain
 * those are the same fact.** One approver signs, the chain completes, and two rows a pixel apart
 * both read `Approved` — the same-value-in-two-slots failure, on the panel a reader opens to see
 * who decided what.
 *
 * **It compares MEANING, never the INSTANT, and that is the whole correction.** Both builders
 * guarded on `completedAt` matching a step's `decidedAt`, on the stated premise that the two "are
 * the SAME moment". They never are: `ApprovalService` reads `_clock.UtcNow` once for the step and
 * again for the request, so the pair differs by a few hundred microseconds —
 * `08:55:16.3787218` against `08:55:16.3792685` on PO-26-00004, and **DIFFERENT on all twelve
 * decided steps in the database**. The guard was real, was never satisfiable, and the duplicate
 * row it existed to prevent rendered on every approved record. A plausible mechanism is not a
 * confirmed one, and two independently sampled instants are never equal.
 *
 * **A tolerance was the other candidate and is worse**: a window wide enough to catch the write
 * is wide enough to swallow a genuinely separate conclusion on a fast sequential chain, and it
 * would be a number nobody can defend. Asking whether a signature already SAYS the outcome needs
 * no window at all.
 *
 * So the outcome row survives exactly where it carries something no signature does — a chain of
 * ACKNOWLEDGEMENTS that nonetheless approves, a RETRACTION, a CANCELLATION, and a conclusion
 * reached with nothing decided at all.
 */
export function approvalOutcomeIsRestated(approval: ApprovalRequestDetail): boolean {
  return approval.steps.some(
    (step) =>
      step.decidedAt != null &&
      ((step.status === APPROVAL_STEP_STATUS.approved &&
        approval.status === APPROVAL_STATUS.approved) ||
        (step.status === APPROVAL_STEP_STATUS.rejected &&
          approval.status === APPROVAL_STATUS.rejected)),
  );
}

/**
 * What a recorded decision SAYS, in one place.
 *
 * **Three surfaces decide — the inbox row, the profile banner and the reject form — and a
 * sentence composed at each of them is one they would eventually word differently.** That is
 * the same argument `<RejectApprovalModal>` already makes for raising its own confirmation
 * rather than leaving it to its two callers, applied one layer down to the words themselves.
 *
 * The SUBJECT may be absent: the engine holds a reference for every workflow built so far, and
 * nothing guarantees the next one will. A sentence naming nothing is still a true sentence, so
 * it falls back rather than leaving the verb dangling over an empty string.
 */
export function decisionMessage(verb: string, subject: string | null | undefined): string {
  return verb + ' ' + (subject ?? 'the request') + '.';
}

/** What a decision that did not land says, when the server offered nothing of its own. */
export const DECISION_FAILED = 'The decision could not be recorded.';
